import { useSyncExternalStore } from 'react'
import { toast } from 'react-toastify'

/**
 * Chromium-only event that lets us open the browser's native install dialog
 * ourselves. Not part of lib.dom, so it is declared here.
 */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed', platform: string }>
  prompt: () => Promise<void>
}

export type InstallPromptResult = 'accepted' | 'dismissed' | 'unavailable'

export function isIOSDevice(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/**
 * Whether this browser can install web apps at all — a static capability check,
 * deliberately independent of whether a prompt is currently in hand. Chromium
 * exposes `onbeforeinstallprompt`, iOS installs through the share sheet.
 *
 * Install UI is gated on this rather than on `canPromptInstall()`, so it does
 * not vanish once the (single-use) prompt event has been spent.
 */
export function isInstallSupported(): boolean {
  return isIOSDevice() || 'onbeforeinstallprompt' in window
}

export function isMobileDevice(): boolean {
  const userAgentData = (navigator as unknown as { userAgentData?: { mobile?: boolean } }).userAgentData
  if (typeof userAgentData?.mobile === 'boolean') return userAgentData.mobile
  return isIOSDevice() || /Android|Mobi/i.test(navigator.userAgent)
}

/**
 * The one gate for every install entry point. Phones only — a gallery as a
 * desktop app is more confusing than useful, so it is not offered there even
 * where the browser would support it (see `useWebAppManifest`, which skips
 * desktop as well, so Chrome shows no install icon in the address bar either).
 */
export function shouldOfferAppInstall(): boolean {
  return isMobileDevice() && isInstallSupported()
}

/** True when the page runs as an installed app (home screen icon / app window). */
export function isRunningStandalone(): boolean {
  if ((window.navigator as unknown as { standalone?: boolean }).standalone) return true
  return window.matchMedia('(display-mode: standalone)').matches
}

// The event fires once and possibly before React has mounted, so it is captured
// at module level and replayed to subscribers.
let deferredPrompt: BeforeInstallPromptEvent | null = null
let wasInstalled = false
const listeners = new Set<() => void>()
let isInitialized = false

function notify() {
  listeners.forEach(listener => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function initPwaInstall() {
  if (isInitialized) return
  isInitialized = true

  window.addEventListener('beforeinstallprompt', event => {
    // Suppress Chrome's own mini-infobar — we decide when to ask.
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    notify()
  })

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    wasInstalled = true
    notify()
  })

  // Chrome only offers the install prompt for pages controlled by a service
  // worker with a fetch handler. Dev is skipped so it cannot interfere with HMR.
  //
  // The worker caches nothing but the offline page (see public/sw.js), so a
  // deploy goes live exactly as it would without it. `updateViaCache: 'none'`
  // makes sure sw.js itself is never read from the HTTP cache either.
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      // A failed registration only means "no install prompt" — nothing else breaks.
      navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => undefined)
    })
  }
}

/**
 * Whether the browser has offered us an install prompt. All install UI must be
 * gated on this: if it is false, no dialog would open and the button would be a
 * dead end.
 */
export function canPromptInstall(): boolean {
  return deferredPrompt !== null
}

/**
 * Opens the browser's native install dialog. Must be called from a user gesture.
 * The captured event can only be used once, whatever the outcome.
 */
export async function promptInstall(): Promise<InstallPromptResult> {
  const event = deferredPrompt
  if (!event) return 'unavailable'

  deferredPrompt = null
  notify()

  try {
    await event.prompt()
    const { outcome } = await event.userChoice
    return outcome
  } catch {
    return 'unavailable'
  }
}

/**
 * Opens the browser's install dialog. The prompt event is single-use and never
 * exists when the app is already installed — install entries stay visible
 * regardless, so that case is answered with a hint instead of a dead button.
 * `unavailableHint` comes from the caller so the text stays translatable.
 */
export async function requestInstall(unavailableHint: string) {
  const outcome = await promptInstall()
  if (outcome === 'unavailable') {
    toast(unavailableHint, { closeButton: true, autoClose: 8000 })
  }
}

function getInstalledSnapshot(): boolean {
  return wasInstalled
}

export function usePwaInstall() {
  const canPrompt = useSyncExternalStore(subscribe, canPromptInstall)
  const isInstalled = useSyncExternalStore(subscribe, getInstalledSnapshot)
  return { canPrompt, isInstalled, promptInstall }
}
